/**
 * PeptidesTab.tsx
 *
 * Patient-detail "Peptides" section — multi-item peptide / Rx orders via Axiom.
 *
 * The order is always created on submit, then the charge is collected against it
 * (charge_patient). When the practice sets `paymentEnforced`, the server parks
 * the order in 'awaiting_payment' and never sends it to the pharmacy until the
 * fee is collected — so abandoning the payment modal leaves a visible, held
 * order with a "Finish payment" button rather than losing the work.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Syringe } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { IntegrationOrdersSubTab } from './labs/IntegrationOrdersSubTab';
import { useAxiomOrderFlow } from './labs/useAxiomOrderFlow';
import { submitDelayPhrase } from '@/lib/peptideOrder';
import { linkedOrderIdFor, type LinkedOrder } from './labs/linkedOrder';

/** Labels for a given grace window; `scheduled` is the only one that varies. */
export function axiomFulfillmentLabels(submitDelayMinutes?: number | null): Record<string, string> {
  return {
    ...AXIOM_FULFILLMENT_LABELS,
    scheduled: `Scheduled — sends ${submitDelayPhrase(submitDelayMinutes)}`,
  };
}

export const AXIOM_FULFILLMENT_LABELS: Record<string, string> = {
  awaiting_payment: 'Awaiting payment',
  scheduled: 'Scheduled — sends in ~1h',
  submitting: 'Submitting',
  received: 'Received',
  routed: 'Routed',
  in_production: 'In production',
  ready_to_pack: 'Preparing',
  ready_to_ship: 'Ready to ship',
  shipped: 'Shipped',
  completed: 'Delivered',
  cancelled: 'Cancelled',
  failed: 'Failed',
  error: 'Error',
};

/** Plain-language meaning of each Axiom status — shown from the Status column's info icon. */
export const AXIOM_STATUS_HELP: Record<string, string> = {
  awaiting_payment: 'The practice requires payment before an order is sent. Nothing has been sent to Axiom and nothing will be until the fee is collected — take the payment from the patient’s chart to release it.',
  scheduled: 'Queued with a 1-hour grace period before it is sent to Axiom. Cancel it here any time until then; once it sends, cancellation follows the pharmacy rules below.',
  submitting: 'The order is being sent to Axiom.',
  received: 'Axiom received the order and is reviewing the prescription. It can still be cancelled.',
  routed: 'Accepted and routed to the fulfilling pharmacy — the last point where it can be cancelled.',
  in_production: 'The compounded peptides are being produced. The pharmacy no longer allows cancellation.',
  ready_to_pack: 'Production finished — the order is being prepared for packaging.',
  ready_to_ship: 'Packaged and waiting for carrier pickup.',
  shipped: 'In transit to the patient — the tracking number appears under the status.',
  completed: 'Delivered to the shipping address on the order.',
  cancelled: 'The order was cancelled before fulfillment; any pending charge was voided.',
  error: 'Something went wrong — the reason shows in red under the status. Contact support if unclear.',
};

interface PeptidesTabProps {
  patientId: number;
  /** A link straight to one order (linkedOrder.ts): opens its details on arrival. */
  linkedOrder?: LinkedOrder | null;
  onLinkedOrderOpened?: () => void;
}

export function PeptidesTab({ patientId, linkedOrder, onLinkedOrderOpened }: PeptidesTabProps) {
  const queryClient = useQueryClient();
  // Two independent Axiom-family flows: pens ('axiom') and vials ('axiom_vials').
  // Each queries the shared enabled-integrations cache and its own onboarding
  // state (vials onboarding is satisfied by the pens enrollment server-side).
  //
  // New orders go through the pens flow as ONE combined cart when the account
  // has both modules (combineVials): pens + vials in a single checkout, split
  // into two orders on submit. Row actions (edit, re-order, …) stay on each
  // table's own flow, since they act on one existing order of one module.
  const pensFlow = useAxiomOrderFlow({
    combineVials: true,
    onRefresh: () => {
      void queryClient.invalidateQueries({ queryKey: ['patient-axiom-orders', patientId] });
      // A combined cart can also place a vials order.
      void queryClient.invalidateQueries({ queryKey: ['patient-axiom_vials-orders', patientId] });
    },
  });
  const vialsFlow = useAxiomOrderFlow({
    integrationKey: 'axiom_vials',
    onRefresh: () => queryClient.invalidateQueries({ queryKey: ['patient-axiom_vials-orders', patientId] }),
  });
  const pens = pensFlow.axiom;
  const vials = vialsFlow.axiom;
  const both = !!pens && !!vials;
  // One "Order Peptides" button for the combined cart. Falls back to the two
  // per-line buttons only in the odd state where vials is usable but pens is
  // not onboarded — the combined cart rides on the pens onboarding.
  const combinedOrder = pensFlow.combined && !(vialsFlow.onboarded && !pensFlow.onboarded);
  // Which orders table is visible when both integrations are enabled; with a
  // single integration there are no tabs and its table renders directly.
  // A link to a vial order lands on Vial Orders.
  const [activeTab, setActiveTab] = useState<'pens' | 'vials'>(
    () => (linkedOrder?.vendorKey === 'axiom_vials' ? 'vials' : 'pens'),
  );
  const showPens = !!pens && (!both || activeTab === 'pens');
  const showVials = !!vials && (!both || activeTab === 'vials');

  return (
    <div className="space-y-4 py-2">
      {/*
        SCRUM-1426. Both order buttons are `whitespace-nowrap shrink-0` (Button's base),
        so the pair is ~330px and cannot compress; with the title beside them the row is
        wider than a 375px viewport and "Order Peptide Vials" was cut to "Order".
      */}
      <div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Peptides</h2>
        <div className="flex items-center gap-2 max-md:w-full max-md:flex-wrap">
          {combinedOrder && (
            <Button
              size="sm"
              disabled={!pensFlow.onboarded}
              title={pensFlow.onboarded ? undefined : 'Your practice must complete Axiom onboarding (Admin → Integrations → Axiom) before ordering.'}
              onClick={() => pensFlow.startNewOrder(patientId)}
            >
              <Syringe className="h-3.5 w-3.5 mr-1" />
              Order Peptides
            </Button>
          )}
          {pens && !combinedOrder && (
            <Button
              size="sm"
              disabled={!pensFlow.onboarded}
              title={pensFlow.onboarded ? undefined : 'Your practice must complete Axiom onboarding (Admin → Integrations → Axiom) before ordering.'}
              onClick={() => pensFlow.startNewOrder(patientId)}
            >
              <Syringe className="h-3.5 w-3.5 mr-1" />
              Order Peptide Pens
            </Button>
          )}
          {vials && !combinedOrder && (
            <Button
              size="sm"
              disabled={!vialsFlow.onboarded}
              title={vialsFlow.onboarded ? undefined : 'Your practice must complete Axiom onboarding (Admin → Integrations → Axiom) before ordering.'}
              onClick={() => vialsFlow.startNewOrder(patientId)}
            >
              <Syringe className="h-3.5 w-3.5 mr-1" />
              Order Peptide Vials
            </Button>
          )}
        </div>
      </div>

      {!pens && !vials && (
        <p className="py-8 text-center text-sm text-slate-500">
          Peptide ordering is not enabled for this practice. Please contact your practice administrator to
          add this feature to your account.
        </p>
      )}

      {both && (
        /* Two tabs fit today, but this is the same idiom as the Labs strip — give it
           the scroller now rather than when a third arrives. */
        <div className="flex items-center gap-1 border-b border-slate-200 max-md:overflow-x-auto max-md:scrollbar-hide">
          {([
            { key: 'pens', label: 'Pen Orders' },
            { key: 'vials', label: 'Vial Orders' },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors max-md:shrink-0 max-md:whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-b-2 border-blue-600 text-blue-700'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {showPens && (
        <div className="space-y-2">
          <IntegrationOrdersSubTab
            patientId={patientId}
            integrationKey="axiom"
            fulfillmentLabels={axiomFulfillmentLabels(pensFlow.axiom?.submitDelayMinutes)}
            statusHelp={AXIOM_STATUS_HELP}
            hideCancelled
            emptyMessage="No orders yet."
            onCancel={(o) => void pensFlow.cancel(o, patientId)}
            onFinishPayment={(o) => pensFlow.finishPayment(o, patientId)}
            onEdit={(o) => pensFlow.edit(o, patientId)}
            onClone={(o) => pensFlow.clone(o, patientId)}
            onResend={(o) => pensFlow.resend(o, patientId)}
            onReorder={(o) => pensFlow.reorder(o, patientId)}
            onRetry={(o) => void pensFlow.retry(o, patientId)}
            openOrderId={linkedOrderIdFor(linkedOrder, 'axiom')}
            onOrderOpened={onLinkedOrderOpened}
          />
        </div>
      )}

      {showVials && (
        <div className="space-y-2">
          <IntegrationOrdersSubTab
            patientId={patientId}
            integrationKey="axiom_vials"
            fulfillmentLabels={axiomFulfillmentLabels(vialsFlow.axiom?.submitDelayMinutes)}
            statusHelp={AXIOM_STATUS_HELP}
            hideCancelled
            emptyMessage="No orders yet."
            onCancel={(o) => void vialsFlow.cancel(o, patientId)}
            onFinishPayment={(o) => vialsFlow.finishPayment(o, patientId)}
            onEdit={(o) => vialsFlow.edit(o, patientId)}
            onClone={(o) => vialsFlow.clone(o, patientId)}
            onResend={(o) => vialsFlow.resend(o, patientId)}
            onReorder={(o) => vialsFlow.reorder(o, patientId)}
            onRetry={(o) => void vialsFlow.retry(o, patientId)}
            openOrderId={linkedOrderIdFor(linkedOrder, 'axiom_vials')}
            onOrderOpened={onLinkedOrderOpened}
          />
        </div>
      )}

      {pensFlow.modals}
      {vialsFlow.modals}
    </div>
  );
}
